import { apiClient } from "./apiClient";
import type {
  TriggerProductionMigrationDto,
  ProductionMigrationStatusDto,
  ProductionMigrationResponse,
  ProductionMigrationLog,
  CancelMigrationResponse,
} from "../types/odoo-migration";

export const odooProductionMigrationService = {
  /**
   * Ejecutar migración manual de emergencia
   * JWT agregado automáticamente por interceptores
   */
  async triggerManualMigration(
    migrationDto: TriggerProductionMigrationDto,
  ): Promise<ProductionMigrationResponse> {
    const response = await apiClient.post<ProductionMigrationResponse>(
      "/odoo-production-migration/trigger-manual",
      migrationDto,
    );
    return response.data;
  },

  /**
   * Obtener estado actual de la migración
   */
  async getMigrationStatus(): Promise<ProductionMigrationStatusDto> {
    const response = await apiClient.get<ProductionMigrationStatusDto>(
      "/odoo-production-migration/status",
    );
    return response.data;
  },

  /**
   * Cancelar migración en progreso
   */
  async cancelMigration(): Promise<CancelMigrationResponse> {
    const response = await apiClient.post<CancelMigrationResponse>(
      "/odoo-production-migration/cancel",
    );
    return response.data;
  },

  /**
   * Obtener logs de migración (últimos 10)
   */
  async getMigrationLogs(): Promise<ProductionMigrationLog[]> {
    const response = await apiClient.get<ProductionMigrationLog[]>(
      "/odoo-production-migration/logs",
    );
    return response.data;
  },

  /**
   * Obtener logs de migración con límite específico
   */
  async getMigrationLogsWithLimit(
    limit: number,
  ): Promise<ProductionMigrationLog[]> {
    const response = await apiClient.get<ProductionMigrationLog[]>(
      `/odoo-production-migration/logs/${limit}`,
    );
    return response.data;
  },

  /**
   * Ejecutar solo post-migracion (forecast + store sync) sin pg_dump/restore
   */
  async triggerPostMigrationOnly(options?: {
    skipClassification?: boolean;
  }): Promise<ProductionMigrationResponse & { warnings?: string[] }> {
    const params = options?.skipClassification
      ? "?skipClassification=true"
      : "";
    const response = await apiClient.post<
      ProductionMigrationResponse & { warnings?: string[] }
    >(`/odoo-production-migration/run-post-migration${params}`);
    return response.data;
  },

  /**
   * Polling del estado de migración con intervalo adaptativo.
   * - Mientras isRunning: polling cada `interval` ms (default 2s)
   * - Cuando termina (isRunning pasa de true a false): poll final inmediato
   * - Cuando inactivo: polling cada 30s
   * - Tras 3 ciclos inactivos consecutivos: detiene polling
   */
  startStatusPolling(
    callback: (status: ProductionMigrationStatusDto) => void,
    interval: number = 2000,
  ): () => void {
    let currentInterval = interval;
    let intervalId: NodeJS.Timeout;
    let idleCycles = 0;
    let wasRunning = false;
    const IDLE_INTERVAL = 30000; // 30s cuando inactivo
    const MAX_IDLE_CYCLES = 3; // Detener después de 3 ciclos inactivos

    const poll = async () => {
      try {
        const status = await this.getMigrationStatus();
        callback(status);

        if (status.isRunning) {
          idleCycles = 0;
          wasRunning = true;
          // Si está corriendo, usar intervalo normal
          if (currentInterval !== interval) {
            clearInterval(intervalId);
            currentInterval = interval;
            intervalId = setInterval(poll, currentInterval);
          }
        } else {
          // Transición running -> stopped: hacer un poll final tras breve delay
          // para garantizar que el frontend capture el estado 100%
          if (wasRunning) {
            wasRunning = false;
            idleCycles = 0;
            setTimeout(async () => {
              try {
                const finalStatus = await this.getMigrationStatus();
                callback(finalStatus);
              } catch {
                /* ignore */
              }
            }, 1500);
          }

          idleCycles++;
          // Detener polling completamente después de MAX_IDLE_CYCLES inactivos
          if (idleCycles >= MAX_IDLE_CYCLES) {
            clearInterval(intervalId);
            return;
          }
          // Usar intervalo largo cuando inactivo
          if (currentInterval !== IDLE_INTERVAL) {
            clearInterval(intervalId);
            currentInterval = IDLE_INTERVAL;
            intervalId = setInterval(poll, currentInterval);
          }
        }
      } catch (error) {
        console.error("Error polling migration status:", error);
      }
    };

    // Primera ejecución inmediata, luego intervalo
    poll();
    intervalId = setInterval(poll, currentInterval);

    // Retornar función para limpiar el intervalo
    return () => clearInterval(intervalId);
  },
};
